import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { GroupContext } from 'react-aria-components/Group';
import type { ContextValue } from 'react-aria-components/slots';
import { Provider } from 'react-aria-components/slots';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components/TextField';
import { TextField as AriaTextField } from 'react-aria-components/TextField';
import { cva } from 'class-variance-authority';

import { InputValidationContext } from './Input';
import { useLPContextProps } from './utils';

import styles from './styles/TextField.module.css';

const textFieldStyles = cva(styles.field);

interface TextFieldProps extends AriaTextFieldProps {
	ref?: Ref<HTMLDivElement>;
	isValid?: boolean;
}

const TextFieldContext = createContext<ContextValue<TextFieldProps, HTMLDivElement>>(null);

/**
 * A text field allows a user to enter a plain text value with a keyboard.
 *
 * https://react-spectrum.adobe.com/react-aria/TextField.html
 */
const TextField = ({ ref, ...props }: TextFieldProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, TextFieldContext);
	const { isValid, ...fieldProps } = mergedProps;
	return (
		<AriaTextField
			{...fieldProps}
			ref={mergedRef}
			className={composeRenderProps(fieldProps.className, (className, renderProps) =>
				textFieldStyles({ ...renderProps, className }),
			)}
		>
			{composeRenderProps(fieldProps.children, (children, { isInvalid, isDisabled }) => (
				<Provider
					values={[
						[GroupContext, { isInvalid, isDisabled }],
						[InputValidationContext, Boolean(isValid && !isInvalid && !isDisabled)],
					]}
				>
					{children}
				</Provider>
			))}
		</AriaTextField>
	);
};

export { TextField, TextFieldContext, textFieldStyles };
export type { TextFieldProps };
